import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MessageSquare, Send, X, Check, Trash2, CornerDownRight, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Initials({ name }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className="w-6 h-6 rounded-full bg-[#0F2040] text-white flex items-center justify-center text-[9px] font-bold shrink-0">
      {initials}
    </div>
  );
}

export function CommentBubble({ count, onClick, section }) {
  if (!count) return null;
  return (
    <button onClick={(e) => { e.stopPropagation(); onClick(section); }}
      className="inline-flex items-center gap-0.5 text-[9px] font-medium text-[#4ABFCF] hover:text-[#3AAFBF] transition-colors ml-1"
      data-testid={`comment-bubble-${section}`}>
      <MessageSquare className="w-3 h-3" />
      <span>{count}</span>
    </button>
  );
}

export function CommentPanel({ estimateId, section, sectionLabel, onClose }) {
  const [comments, setComments] = useState([]);
  const [newText, setNewText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [posting, setPosting] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchComments = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/estimates/${estimateId}/comments`, { withCredentials: true });
      const sectionComments = (res.data.comments || []).filter(c => c.section === section);
      setComments(sectionComments);
    } catch { /* */ }
    finally { setLoading(false); }
  }, [estimateId, section]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const handlePost = async (text, parentId = null) => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API_URL}/api/estimates/${estimateId}/comments`, {
        section, text: text.trim(), parent_id: parentId,
      }, { withCredentials: true });
      if (parentId) { setReplyText(''); setReplyTo(null); }
      else { setNewText(''); }
      await fetchComments();
    } catch { /* */ }
    finally { setPosting(false); }
  };

  const handleResolve = async (commentId) => {
    try {
      await axios.patch(`${API_URL}/api/estimates/${estimateId}/comments/${commentId}/resolve`, {}, { withCredentials: true });
      await fetchComments();
    } catch { /* */ }
  };

  const handleDelete = async (commentId) => {
    try {
      await axios.delete(`${API_URL}/api/estimates/${estimateId}/comments/${commentId}`, { withCredentials: true });
      await fetchComments();
    } catch { /* */ }
  };

  // Group: root comments + their replies
  const roots = comments.filter(c => !c.parent_id);
  const replies = comments.filter(c => c.parent_id);
  const getReplies = (rootId) => replies.filter(r => r.parent_id === rootId);

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-white border-l border-slate-200 shadow-2xl z-50 flex flex-col"
      data-testid={`comment-panel-${section}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
        <div>
          <p className="text-sm font-semibold text-[#0F2040] flex items-center gap-1.5">
            <MessageSquare className="w-4 h-4 text-[#4ABFCF]" /> Comments
          </p>
          <p className="text-[10px] text-slate-400 mt-0.5">{sectionLabel}</p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-red-500 transition-colors" data-testid="close-comment-panel">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Comments list */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {loading && <div className="text-center py-6"><Loader2 className="w-5 h-5 text-[#4ABFCF] animate-spin mx-auto" /></div>}
        {!loading && roots.length === 0 && (
          <div className="text-center py-8">
            <MessageSquare className="w-8 h-8 text-slate-200 mx-auto mb-2" />
            <p className="text-xs text-slate-400">No comments yet. Start the conversation!</p>
          </div>
        )}
        {roots.map(comment => (
          <div key={comment.id} className={`rounded-lg border transition-colors ${comment.resolved ? 'bg-slate-50/50 border-slate-200/60 opacity-60' : 'bg-white border-slate-200'}`}
            data-testid={`comment-thread-${comment.id}`}>
            {/* Root comment */}
            <div className="px-3 py-2.5">
              <div className="flex items-start gap-2">
                <Initials name={comment.author_name} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-semibold text-[#0F2040]">{comment.author_name}</span>
                    <span className="text-[9px] text-slate-400">
                      {new Date(comment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {comment.resolved && <span className="text-[8px] text-emerald-500 font-semibold">Resolved</span>}
                  </div>
                  <p className={`text-xs text-slate-600 leading-relaxed mt-0.5 ${comment.resolved ? 'line-through' : ''}`}>{comment.text}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <button onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)}
                      className="text-[9px] text-[#4ABFCF] hover:text-[#3AAFBF] font-medium flex items-center gap-0.5"
                      data-testid={`reply-btn-${comment.id}`}>
                      <CornerDownRight className="w-2.5 h-2.5" /> Reply
                    </button>
                    <button onClick={() => handleResolve(comment.id)}
                      className={`text-[9px] font-medium flex items-center gap-0.5 ${comment.resolved ? 'text-amber-500 hover:text-amber-600' : 'text-emerald-500 hover:text-emerald-600'}`}
                      data-testid={`resolve-btn-${comment.id}`}>
                      <Check className="w-2.5 h-2.5" /> {comment.resolved ? 'Reopen' : 'Resolve'}
                    </button>
                    <button onClick={() => handleDelete(comment.id)}
                      className="text-[9px] text-slate-400 hover:text-red-500 font-medium flex items-center gap-0.5"
                      data-testid={`delete-btn-${comment.id}`}>
                      <Trash2 className="w-2.5 h-2.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Replies */}
            {getReplies(comment.id).length > 0 && (
              <div className="px-3 pb-2 ml-8 space-y-2 border-t border-slate-100 pt-2">
                {getReplies(comment.id).map(reply => (
                  <div key={reply.id} className="flex items-start gap-2" data-testid={`reply-${reply.id}`}>
                    <Initials name={reply.author_name} />
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-semibold text-[#0F2040]">{reply.author_name}</span>
                        <span className="text-[9px] text-slate-400">
                          {new Date(reply.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed mt-0.5">{reply.text}</p>
                      <button onClick={() => handleDelete(reply.id)}
                        className="text-[9px] text-slate-400 hover:text-red-500 mt-0.5 flex items-center gap-0.5">
                        <Trash2 className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Reply input */}
            {replyTo === comment.id && (
              <div className="px-3 pb-2.5 ml-8 border-t border-slate-100 pt-2">
                <div className="flex items-end gap-1.5">
                  <textarea value={replyText} onChange={e => setReplyText(e.target.value)}
                    rows={1} placeholder="Write a reply..."
                    className="flex-1 border rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-[#4ABFCF] focus:border-[#4ABFCF] resize-none"
                    data-testid={`reply-input-${comment.id}`} />
                  <button onClick={() => handlePost(replyText, comment.id)} disabled={posting || !replyText.trim()}
                    className="w-7 h-7 bg-[#4ABFCF] text-white rounded flex items-center justify-center hover:bg-[#3AAFBF] disabled:opacity-50"
                    data-testid={`reply-send-${comment.id}`}>
                    {posting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* New comment input */}
      <div className="px-4 py-3 border-t border-slate-200 bg-slate-50">
        <div className="flex items-end gap-2">
          <textarea value={newText} onChange={e => setNewText(e.target.value)}
            rows={2} placeholder="Add a comment..."
            className="flex-1 border rounded-lg px-3 py-2 text-xs focus:ring-1 focus:ring-[#4ABFCF] focus:border-[#4ABFCF] resize-none"
            data-testid="new-comment-input" />
          <button onClick={() => handlePost(newText)} disabled={posting || !newText.trim()}
            className="w-9 h-9 bg-[#0F2040] text-white rounded-lg flex items-center justify-center hover:bg-[#1A3055] disabled:opacity-50 shrink-0"
            data-testid="new-comment-send">
            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
